import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { Logo } from './logo';

export type BrandLockupProps = Omit<React.ComponentProps<'span'>, 'children'> & {
  /**
   * The sub-app wordmark rendered after the Noor logo — "eats", "delivery", …
   * Omit it on surfaces that belong to no single sub-app (the login screen, the
   * welcome picker), which show the bare logo.
   */
  suffix?: React.ReactNode;
};

/**
 * The header brand lockup: the Noor wordmark plus the current sub-app's name,
 * set in muted gray to its right (Figma "Nav bar" 119:8071). It is what tells a
 * merchant *which* sub-app they are in — the super-app hosts several and the
 * rest of the chrome looks identical between them.
 *
 * The suffix is the untranslated brand wordmark ("noor eats"), not the localized
 * catalog label from `common:subApps.<id>` — "noor Еда" would read as a
 * translation of the brand rather than a product name.
 */
function BrandLockup({ className, suffix, ...props }: BrandLockupProps) {
  return (
    <span
      data-slot="brand-lockup"
      className={cn('flex items-end gap-1.5', className)}
      {...props}
    >
      <Logo className="text-primary" />
      {suffix !== undefined ? (
        <span
          data-slot="brand-lockup-suffix"
          // `items-end` aligns to the SVG's box, whose bottom edge is the
          // wordmark's baseline; the -1px lifts the text off it the way the
          // Figma lockup sits (suffix bottom 44.5 of the logo's 46-unit box).
          className="relative -top-px text-xl leading-none font-normal tracking-tight text-muted-foreground"
        >
          {suffix}
        </span>
      ) : null}
    </span>
  );
}

export { BrandLockup };
